import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import styles from "./styles.module.css";
import { Box, Button, DialogActions, DialogContent, Grid, Autocomplete, TextField } from "@mui/material";
import GenericTextField from "../../components/GenericTextField";

const BancaProtheusFormSchema = z
  .object({
    id_conciliacao_conta_bancaria: z.number().readonly(),
    codigo_banco: z.string(),
    agencia: z.string(),
    conta_bancaria: z.string(),
    conta_protheus: z.string(),
    conta_dominio: z.string(),
    tipo_conta: z.enum(['CONTA-CORRENTE', 'CONTA-APLICAÇÃO', 'CONTA-INVESTIMENTO']),
  })
  .partial({
    id_conciliacao_conta_bancaria: true,
  })
  .required({
    codigo_banco: true,
    agencia: true,
    conta_bancaria: true,
    conta_protheus: true,
    conta_dominio: true,
    tipo_conta: true,
  });

type BancaProtheusFormData = z.infer<typeof BancaProtheusFormSchema>;

type BancaProtheusFormProps = {
  bancaProtheusValues: BancaProtheusFormData;
  onSubmit(data: BancaProtheusFormData): void;
  onClose(event: any): void;
};

export default function BancaProtheusForm({
  bancaProtheusValues,
  onSubmit,
  onClose,
}: BancaProtheusFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<BancaProtheusFormData>({
    mode: "onChange",
    resolver: zodResolver(BancaProtheusFormSchema),
    defaultValues: {
      id_conciliacao_conta_bancaria:
        bancaProtheusValues.id_conciliacao_conta_bancaria || undefined,
      codigo_banco: bancaProtheusValues.codigo_banco,
      agencia: bancaProtheusValues.agencia,
      conta_bancaria: bancaProtheusValues.conta_bancaria,
      conta_protheus: bancaProtheusValues.conta_protheus,
      conta_dominio: bancaProtheusValues.conta_dominio,
      tipo_conta: bancaProtheusValues.tipo_conta || "CONTA-CORRENTE",
    },
  });

  const handleCloseForm = (event: any) => {
    onClose(event);
  };

  const submit = (data: BancaProtheusFormData) => {
    const body: any = {
      contaBancaria: {
        id_conciliacao_conta_bancaria: data.id_conciliacao_conta_bancaria,
        codigoBanco: data.codigo_banco,
        agencia: data.agencia,
        contaBancaria: data.conta_bancaria,
        contaProtheus: data.conta_protheus,
        contaDominio: data.conta_dominio,
        tipoConta: data.tipo_conta,
      },
    };

    onSubmit(body);
  };

  return (
    <form onSubmit={handleSubmit(submit)} className={styles.formDiv}>
      <DialogContent className={styles.formContainer}>
        <Box className={styles.formContent}>
          <Grid container spacing={2}>
            <Grid item xs={1}>
              <Controller
                name="id_conciliacao_conta_bancaria"
                control={control}
                defaultValue={bancaProtheusValues.id_conciliacao_conta_bancaria}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    disabled
                    label="ID"
                    error={Boolean(errors.id_conciliacao_conta_bancaria)}
                    helperText={errors.id_conciliacao_conta_bancaria?.message}
                    placeholder={"Nova Conta Bancária"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={3}>
              <Controller
                name="codigo_banco"
                control={control}
                defaultValue={bancaProtheusValues.codigo_banco}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    label="Código do Banco"
                    error={Boolean(errors.codigo_banco)}
                    helperText={errors.codigo_banco?.message}
                    placeholder={"Código do Banco"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={3}>
              <Controller
                name="agencia"
                control={control}
                defaultValue={bancaProtheusValues.agencia}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    label="Agência"
                    error={Boolean(errors.agencia)}
                    helperText={errors.agencia?.message}
                    placeholder={"Agência"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={5}>
              <Controller
                name="conta_bancaria"
                control={control}
                defaultValue={bancaProtheusValues.conta_bancaria}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    label="Conta"
                    error={Boolean(errors.conta_bancaria)}
                    helperText={errors.conta_bancaria?.message}
                    placeholder={"Conta Bancária"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="conta_protheus"
                control={control}
                defaultValue={bancaProtheusValues.conta_protheus}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    label="Conta Protheus"
                    error={Boolean(errors.conta_protheus)}
                    helperText={errors.conta_protheus?.message}
                    placeholder={"Conta Protheus"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="conta_dominio"
                control={control}
                defaultValue={bancaProtheusValues.conta_dominio}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    label="Conta Domínio"
                    error={Boolean(errors.conta_dominio)}
                    helperText={errors.conta_dominio?.message}
                    placeholder={"Conta Domínio"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
            {/*TIPO CONTA*/}
            <Grid item xs={6}>
              <Controller
                name="tipo_conta"
                control={control}
                defaultValue={bancaProtheusValues.tipo_conta || "CONTA-CORRENTE"}
                render={({ field: { ref, onChange, value, ...field } }) => (
                  <Autocomplete
                    options={['CONTA-CORRENTE', 'CONTA-APLICAÇÃO', 'CONTA-INVESTIMENTO']}
                    getOptionLabel={(option) => option}
                    onChange={(_, value) => onChange(value)}
                    value={value || null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tipo de Conta"
                        error={Boolean(errors.tipo_conta)}
                        helperText={errors.tipo_conta?.message}
                        placeholder={"Tipo de Conta"}
                        inputRef={ref}
                        {...field}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
        <Box>
          <DialogActions className={styles.botoesPopupGeral}>
            <Button
              className={styles.botaoPopupCancelar}
              onClick={handleCloseForm}
              size="small"
            >
              Cancelar
            </Button>
            <Button
              className={styles.botaoPopupSalvar}
              type="submit"
              color="primary"
              size="small"
            >
              Salvar
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </form>
  );
}
